import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { statsService } from '../services/statsService'
import DoglegScoreChip from './DoglegScoreChip'
import { getInitials } from '../utils/avatarUtils'
import { formatDate } from '../utils/dateFormat'
import getDisplayName from '../utils/courseNameUtils'

function PlayerRow({ entry, rank, isMe, navigate }) {
  return (
    <div className={`flex items-center gap-3 py-2.5 px-2 rounded-lg ${isMe ? 'bg-green-50' : ''}`}>
      {rank !== undefined && (
        <span className={`w-6 text-center font-bold tabular-nums ${rank <= 3 ? 'text-amber-600' : 'text-gray-400'}`}>
          {rank}
        </span>
      )}
      <button
        onClick={() => entry.username && navigate(`/profile/${entry.username}`)}
        className="flex items-center gap-2 flex-1 min-w-0 py-1 hover:opacity-80 text-left"
      >
        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center shrink-0">
          {entry.avatar_url ? (
            <img src={entry.avatar_url} alt={entry.username} className="w-full h-full rounded-full object-cover" />
          ) : (
            <span className="text-green-700 font-semibold text-xs">
              {getInitials(entry) || entry.username?.[0]?.toUpperCase() || '?'}
            </span>
          )}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate">
            {entry.username || 'Golfer'}{isMe ? ' (you)' : ''}
          </div>
          <div className="text-xs text-gray-500">{formatDate(entry.date)}</div>
        </div>
      </button>
      {entry.dogleg_score !== null && entry.dogleg_score !== undefined && (
        <DoglegScoreChip score={entry.dogleg_score} isOwn={false} />
      )}
      <button
        onClick={() => entry.short_code && navigate(`/rounds/${entry.short_code}`)}
        className="text-right py-1 px-1 -mr-1 hover:text-green-700"
      >
        <span className="text-lg font-bold text-gray-900 tabular-nums">{entry.total_score}</span>
        {entry.holes_played === 9 && (
          <span className="block text-[10px] leading-none text-gray-400">9 holes</span>
        )}
      </button>
    </div>
  )
}

function CoursePage() {
  const { courseId } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [page, setPage] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      const { data, error } = await statsService.getCoursePage(courseId)
      if (cancelled) return
      if (error) setError(error)
      else setPage(data)
      setLoading(false)
    }
    if (courseId) load()
    return () => { cancelled = true }
  }, [courseId])

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-2 sm:p-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 animate-pulse">
          <div className="h-7 bg-gray-200 rounded w-1/2 mb-3"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-4 gap-3">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-16 bg-gray-100 rounded"></div>)}
          </div>
        </div>
      </div>
    )
  }

  if (error || !page?.course) {
    return (
      <div className="max-w-4xl mx-auto p-2 sm:p-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
          <div className="text-5xl mb-3">⛳</div>
          <h2 className="text-lg font-semibold text-gray-800 mb-1">Course page unavailable</h2>
          <p className="text-gray-500 text-sm">
            {error ? 'Course pages need the stats engine (database/stats_layer.sql).' : 'Course not found.'}
          </p>
          <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2.5 text-sm bg-gray-100 rounded-lg hover:bg-gray-200">
            Go back
          </button>
        </div>
      </div>
    )
  }

  const c = page.course
  const courseTitle = getDisplayName(c)
  // When the title falls back to the club name, don't repeat it below
  const subtitle = [c.club_name, c.city, c.state]
    .filter(Boolean).filter(part => part !== courseTitle).join(' · ')
  const leaderboard = page.leaderboard || []
  const recent = page.recent_rounds || []

  return (
    <div className="max-w-4xl mx-auto p-2 sm:p-4">
      <div className="space-y-3 sm:space-y-4">

        {/* Course header — neutral card, dark ink */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6 animate-fade-up">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">⛳ {courseTitle}</h1>
          {subtitle && <p className="mt-1 text-gray-600">{subtitle}</p>}
          <p className="mt-1 text-gray-500 text-sm">
            {c.num_holes || 18} holes{c.total_par ? ` · Par ${c.total_par}` : ''}
          </p>
        </div>

        {/* Stat row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 p-3">
            <div className="text-xs text-gray-500">Rounds logged</div>
            <div className="text-2xl font-semibold text-gray-900 mt-1 tabular-nums">{page.rounds_count || 0}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-3">
            <div className="text-xs text-gray-500">Golfers</div>
            <div className="text-2xl font-semibold text-gray-900 mt-1 tabular-nums">{page.players_count || 0}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-3">
            <div className="text-xs text-gray-500">Average score</div>
            <div className="text-2xl font-semibold text-gray-900 mt-1 tabular-nums">{page.avg_score ?? '—'}</div>
            {page.avg_vs_par !== null && page.avg_vs_par !== undefined && (
              <div className="text-xs text-gray-500 tabular-nums">
                {page.avg_vs_par > 0 ? `+${page.avg_vs_par}` : page.avg_vs_par} vs par
              </div>
            )}
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-3">
            <div className="text-xs text-gray-500">{page.my_best ? 'Your best' : 'Course record'}</div>
            <div className="text-2xl font-semibold text-gray-900 mt-1 tabular-nums">
              {page.my_best ?? page.course_record ?? '—'}
            </div>
            {page.my_best && page.course_record && (
              <div className="text-xs text-gray-500 tabular-nums">record {page.course_record}</div>
            )}
          </div>
        </div>

        {/* Leaderboard */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4">
          <h3 className="font-semibold text-gray-900">🏆 Leaderboard</h3>
          <p className="text-xs text-gray-500 mt-0.5">Each golfer's best 18-hole round here</p>
          <div className="mt-2 divide-y divide-gray-100">
            {leaderboard.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">
                No 18-hole rounds logged here yet — post one and claim the top spot.
              </p>
            ) : (
              leaderboard.map((entry, i) => (
                <PlayerRow key={entry.user_id} entry={entry} rank={i + 1}
                  isMe={entry.user_id === user?.id} navigate={navigate} />
              ))
            )}
          </div>
        </div>

        {/* Recent rounds */}
        {recent.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4">
            <h3 className="font-semibold text-gray-900">Recent rounds</h3>
            <div className="mt-2 divide-y divide-gray-100">
              {recent.map((entry, i) => (
                <PlayerRow key={`${entry.short_code || i}`} entry={entry}
                  isMe={entry.user_id === user?.id} navigate={navigate} />
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

export default CoursePage
